import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  TextField,
  Tabs,
  Tab,
  TextareaAutosize,
  InputAdornment,
  Tooltip,
  Avatar,
  Stack,
  alpha,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import axios from 'axios';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Pending as PendingIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  BusinessCenter as BusinessCenterIcon,
  ReceiptLong as ReceiptLongIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FinanceManagerApproval = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [uploadedPayrolls, setUploadedPayrolls] = useState([]);
  const [selectedExport, setSelectedExport] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Employee list for modal
  const [employeeListDialogOpen, setEmployeeListDialogOpen] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeListLoading, setEmployeeListLoading] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeListExportInfo, setEmployeeListExportInfo] = useState(null);

  useEffect(() => {
    fetchUploadedData();
  }, []);

  const fetchUploadedData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/salary-export/pending-approvals`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setUploadedPayrolls(response.data.data || []);
      }
    } catch (error) {
      //console.error('Error fetching uploaded payrolls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (exportId) => {
    if (!window.confirm('Are you sure you want to approve this payroll?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/approve/${exportId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage('✅ Payroll approved successfully!');
        fetchUploadedData();
        if (detailsDialogOpen) setDetailsDialogOpen(false);
        handleMenuClose();
      }
    } catch (error) {
      //console.error('Error approving payroll:', error);
      alert('Failed to approve payroll. Please try again.');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/reject/${selectedExport.id}`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage('❌ Payroll rejected successfully!');
        setRejectDialogOpen(false);
        setRejectReason('');
        fetchUploadedData();
        if (detailsDialogOpen) setDetailsDialogOpen(false);
        handleMenuClose();
      }
    } catch (error) {
      //console.error('Error rejecting payroll:', error);
      alert('Failed to reject payroll. Please try again.');
    }
  };

  const handleDownloadFile = async (record) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/salary-export/download/${record.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', record.file_name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      handleMenuClose();
    } catch (error) {
      //console.error('Error downloading file:', error);
      alert('Failed to download salary spreadsheet.');
    }
  };

  const handleViewDetails = (record) => {
    setSelectedExport(record);
    setDetailsDialogOpen(true);
    handleMenuClose();
  };

  const handleViewEmployeeList = async (record) => {
    if (!record || !record.id) return;
    setEmployeeListExportInfo(record);
    setEmployeeListDialogOpen(true);
    setEmployeeListLoading(true);
    setEmployeeSearchTerm('');
    handleMenuClose();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/salary-export/export-employees/${record.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setEmployeeList(response.data.data || []);
      }
    } catch (error) {
      //console.error('Error fetching employee details:', error);
    } finally {
      setEmployeeListLoading(false);
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const filteredEmployees = employeeList.filter((emp) => {
    const term = employeeSearchTerm.toLowerCase();
    return (
      (emp.employee_id && emp.employee_id.toLowerCase().includes(term)) ||
      (emp.name && emp.name.toLowerCase().includes(term)) ||
      (emp.bank_name && emp.bank_name.toLowerCase().includes(term)) ||
      (emp.account_number && emp.account_number.includes(term))
    );
  });

  return (
    <Box sx={{ maxWidth: 1400, margin: 'auto', padding: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" color="text.primary" gutterBottom>
          Salary Authorization
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and approve payroll submissions before finalizing bank transfers
        </Typography>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Header with actions */}
        <Box
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PendingIcon color="primary" />
            <Typography variant="h6" fontWeight="600">
              Pending Approvals
            </Typography>
            <Chip
              label={uploadedPayrolls.length}
              size="small"
              color="primary"
              sx={{ ml: 1, fontWeight: 600 }}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchUploadedData}
            startIcon={<RefreshIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : uploadedPayrolls.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No payrolls pending manager approval at this time.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.06),
                      '& th': {
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '0.85rem',
                        letterSpacing: '0.02em',
                      },
                    }}
                  >
                    <TableCell>Month / Year</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell align="center">Employees</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadedPayrolls.map((record) => (
                    <TableRow
                      key={record.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight="500">
                            {record.month_year}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            color: theme.palette.primary.main,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => handleDownloadFile(record)}
                        >
                          <ReceiptLongIcon fontSize="small" />
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {record.file_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<PeopleIcon fontSize="small" />}
                          label={record.total_employees || 0}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" color="primary.main">
                          {formatCurrency(record.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {record.payment_confirmed_by_name || 'Finance Team'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={(event) => handleMenuOpen(event, record)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
      >
        <MenuItem onClick={() => selectedRecord && handleApprove(selectedRecord.id)}>
          <ListItemIcon>
            <CheckCircleIcon color="success" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Approve" sx={{ color: 'success.main' }} />
        </MenuItem>
        
        <MenuItem onClick={() => {
          if (selectedRecord) {
            setSelectedExport(selectedRecord);
            setRejectDialogOpen(true);
            handleMenuClose();
          }
        }}>
          <ListItemIcon>
            <CancelIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Reject" sx={{ color: 'error.main' }} />
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => selectedRecord && handleViewDetails(selectedRecord)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Details" />
        </MenuItem>
        
        <MenuItem onClick={() => selectedRecord && handleViewEmployeeList(selectedRecord)}>
          <ListItemIcon>
            <PeopleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Employees" />
        </MenuItem>
        
        <MenuItem onClick={() => selectedRecord && handleDownloadFile(selectedRecord)}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download File" />
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="600">
              Payroll Details
            </Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          {selectedExport && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    MONTH & YEAR
                  </Typography>
                  <Typography variant="body1" fontWeight="600" sx={{ mt: 0.5 }}>
                    {selectedExport.month_year}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    FILE NAME
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                    {selectedExport.file_name}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    STATUS
                  </Typography>
                  <Chip
                    label="Pending Approval"
                    color="warning"
                    size="small"
                    icon={<PendingIcon />}
                    sx={{ mt: 0.5 }}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    TOTAL PAYOUT
                  </Typography>
                  <Typography variant="h6" fontWeight="700" color="primary.main" sx={{ mt: 0.5 }}>
                    {formatCurrency(selectedExport.total_amount)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    TOTAL EMPLOYEES
                  </Typography>
                  <Typography variant="h6" fontWeight="600" sx={{ mt: 0.5 }}>
                    {selectedExport.total_employees}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    UPLOADED AT
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                    {new Date(selectedExport.payment_confirmed_at).toLocaleString()}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    UPLOADED BY
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                    {selectedExport.payment_confirmed_by_name || 'Finance Team'}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }}>
          {selectedExport && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApprove(selectedExport.id)}
                startIcon={<CheckCircleIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setRejectDialogOpen(true);
                  setDetailsDialogOpen(false);
                }}
                startIcon={<CancelIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
              >
                Reject
              </Button>
            </>
          )}
          <Button
            onClick={() => setDetailsDialogOpen(false)}
            variant="text"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon color="error" />
            <Typography variant="h6" fontWeight="600" color="error.main">
              Reject Payroll
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this bank payout:
          </Typography>
          <TextareaAutosize
            minRows={4}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '14px',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              marginTop: '4px',
              fontFamily: theme.typography.fontFamily,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              resize: 'vertical',
            }}
            placeholder="Enter rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            variant="text"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            Reject Payout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee List Dialog */}
      <Dialog
        open={employeeListDialogOpen}
        onClose={() => setEmployeeListDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" />
              <Typography variant="h6" fontWeight="600">
                Employee Disbursement Details
              </Typography>
            </Box>
            <IconButton onClick={() => setEmployeeListDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <TextField
            size="small"
            placeholder="Search employees..."
            value={employeeSearchTerm}
            onChange={(e) => setEmployeeSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3, width: '100%' }}
          />

          {employeeListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      '& th': {
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '0.8rem',
                        letterSpacing: '0.02em',
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Bank Name</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>IFSC</TableCell>
                    <TableCell align="right">Net Salary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.map((emp, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {emp.employee_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{emp.name}</TableCell>
                      <TableCell>{emp.bank_name}</TableCell>
                      <TableCell>{emp.account_number}</TableCell>
                      <TableCell>{emp.ifsc_code}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" color="primary.main">
                          {formatCurrency(emp.net_salary)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setEmployeeListDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinanceManagerApproval;